import React, { useState, useEffect } from "react";
import { Table, Button, Upload, message, Modal } from "antd";
import {
  UploadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import axios from "axios";
import "./BulkProductUpload.css";

const productFields = [
  "alphaCode",
  "name",
  "height",
  "width",
  "vendor",
  "shape",
  "color",
  "case",
  "description",
  "mass",
  "mouldNumber",
  "suggestions",
  "notAvail",
  "images",
  "pdfLink",
];

const BulkUploadModal = ({ open, onClose, onUploaded }) => {
  const [excelData, setExcelData] = useState([]);
  const [loading, setLoading] = useState(false);
  const url = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("userToken");

  // Clear any previously parsed sheet when the modal is dismissed, so
  // reopening never shows a stale preview from an earlier attempt.
  useEffect(() => {
    if (!open) {
      setExcelData([]);
    }
  }, [open]);

  const handleDownloadTemplate = () => {
    const sampleData = [
      {
        alphaCode: "ALPHA001",
        name: "ALPHA001",
        height: "10",
        width: "5",
        vendor: "VendorX",
        shape: "Round",
        color: "Red",
        case: "Case1",
        description: "Sample description",
        mass: "50",
        mouldNumber: "M001",
        suggestions: "Option1, Option2",
        notAvail: "CountryA, CountryB",
        images: "https://example.com/img1.jpg, https://example.com/img2.jpg",
        pdfLink: "https://example.com/sample.pdf",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData, {
      header: productFields,
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
    XLSX.writeFile(workbook, "Product_Template.xlsx");
  };

  const handleExcelUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        message.error("No data found in Excel file");
        return;
      }

      const mappedData = jsonData.map((item) => ({
        ...item,
        name: item.alphaCode,
      }));

      setExcelData(mappedData);
      message.success("Excel data loaded successfully!");
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleContinue = async () => {
    if (excelData.length === 0) {
      message.error("Please upload Excel first!");
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${url}/auth/v1/product/bulk`,
        { products: excelData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Products uploaded successfully!");
      setExcelData([]);
      // Let the products list refresh before the modal closes.
      if (onUploaded) onUploaded();
      if (onClose) onClose();
    } catch (error) {
      console.error(error);
      message.error(
        error.response?.data?.message || "Failed to upload products"
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = productFields.map((field) => ({
    title: field.toUpperCase(),
    dataIndex: field,
    key: field,
    render: (val) => (Array.isArray(val) ? val.join(", ") : val || "-"),
  }));

  return (
    <Modal
      title="Bulk Upload Products"
      open={open}
      onCancel={onClose}
      centered
      width={1080}
      zIndex={2000}
      className="product-modal bulk-upload-modal"
      footer={
        <div className="product-modal-actions">
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            className="product-modal-submit"
            icon={<CheckCircleOutlined />}
            loading={loading}
            disabled={excelData.length === 0}
            onClick={handleContinue}
          >
            {excelData.length > 0
              ? `Upload ${excelData.length} product${
                  excelData.length === 1 ? "" : "s"
                }`
              : "Upload"}
          </Button>
        </div>
      }
    >
      <div className="bulk-upload-toolbar">
        <Button icon={<FileExcelOutlined />} onClick={handleDownloadTemplate}>
          Download Excel Template
        </Button>
        <Upload
          beforeUpload={handleExcelUpload}
          showUploadList={false}
          accept=".xlsx, .xls"
        >
          <Button type="primary" icon={<UploadOutlined />}>
            Upload Excel
          </Button>
        </Upload>
      </div>

      {excelData.length > 0 ? (
        <>
          <p className="bulk-upload-count">
            {excelData.length} row{excelData.length === 1 ? "" : "s"} ready to
            upload. Review below, then confirm.
          </p>
          <Table
            columns={columns}
            dataSource={excelData}
            rowKey={(record, index) => index}
            bordered
            pagination={false}
            size="small"
            scroll={{ x: 1400 }}
          />
        </>
      ) : (
        <div className="bulk-upload-empty">
          <InboxOutlined className="bulk-upload-empty-icon" />
          <p className="bulk-upload-empty-title">No file selected</p>
          <p className="bulk-upload-empty-text">
            Download the template, fill it in, then upload it here to preview
            your products before they are added.
          </p>
        </div>
      )}
    </Modal>
  );
};

export default BulkUploadModal;
